import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Mic,
  MicOff,
  X,
  Send,
  FileText,
  MessageSquare,
  Calendar,
  TrendingUp,
  Zap,
  ArrowLeft,
  Bug,
  Trash2,
  Square,
  AudioWaveform,
  FileSearch,
  Brain,
  Search,
  BookOpen,
  PenLine,
  Loader2,
  Volume2,
} from "lucide-react";
import { useAIAssistant, popularCommands, DebugLog } from "@/hooks/useAIAssistant";
import { AgentSteps, AgentText } from "@/components/ai/AgentText";
import { userAvatar } from "@/utils/userAvatar";
import { useAuth } from "@/contexts/AuthContext";
// @ts-ignore
import aiArtIcon from "@/assets/images/ai-art-icon.png";
// @ts-ignore
import aiArtIconWhite from "@/assets/images/ai-art-icon-white.png";

interface AIAssistantProps {
  isOpen: boolean;
  onClose: () => void;
}

// Вынесено из компонента для избежания пересоздания
const getCommandIcon = (id: string) => {
  if (id.includes("schedule")) return Calendar;
  if (id.includes("report") || id.includes("summary")) return FileText;
  if (id.includes("message")) return MessageSquare;
  if (id.includes("top") || id.includes("regions")) return TrendingUp;
  return Zap;
};

// Компонент для эффекта печатания
interface TypewriterTextProps {
  text: string;
  speed?: number;
  onComplete?: () => void;
}

function TypewriterText({ text, speed = 15, onComplete }: TypewriterTextProps) {
  const [displayedText, setDisplayedText] = useState("");
  const [isComplete, setIsComplete] = useState(false);
  const onCompleteRef = useRef(onComplete);

  // Обновляем ref при изменении callback
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!text) return;

    let index = 0;
    setDisplayedText("");
    setIsComplete(false);

    const timer = setInterval(() => {
      if (index < text.length) {
        // Добавляем по несколько символов за раз для более быстрой печати
        const charsToAdd = Math.min(3, text.length - index);
        index += charsToAdd;
        setDisplayedText(text.substring(0, index));
      } else {
        clearInterval(timer);
        setIsComplete(true);
        onCompleteRef.current?.();
      }
    }, speed);

    return () => clearInterval(timer);
  }, [text, speed]); // Убрали onComplete из зависимостей

  // Разметка разбирается и во время набора: иначе звёздочки `**` мелькали бы
  // на экране, пока не закроется пара (парсер переживает оборванную строку).
  return (
    <span>
      <AgentText text={displayedText} />
      {!isComplete && <span className="animate-pulse">▊</span>}
    </span>
  );
}

export function AIAssistant({ isOpen, onClose }: AIAssistantProps) {
  const { user } = useAuth();
  const {
    isListening,
    isProcessing,
    isTranscribing,
    isThinking,
    agentStep,
    isSpeaking,
    currentConversation,
    startListening,
    stopListening,
    handleUserInput,
    stopTranscribing,
    stopThinking,
    stopSpeaking,
    debugLogs,
    showDebug,
    toggleDebug,
    clearLogs,
  } = useAIAssistant();

  const [textInput, setTextInput] = useState("");
  const [showQuickCommands, setShowQuickCommands] = useState(true);
  const [showChatList, setShowChatList] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [thinkingPhase, setThinkingPhase] = useState(0);
  const [typingMessageId, setTypingMessageId] = useState<string | null>(null);
  const typedMessagesRef = useRef<Set<string>>(new Set()); // Уже напечатанные сообщения

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useRef(`ai-assistant-title-${Date.now()}`).current;

  // Мемоизация hasMessages
  const hasMessages = useMemo(
    () => (currentConversation?.messages.length || 0) > 0,
    [currentConversation?.messages.length]
  );

  // Сообщения для STT
  const sttMessages = useMemo(
    () => [
      { text: "Распознаю речь...", Icon: Mic },
      { text: "Обрабатываю аудио...", Icon: AudioWaveform },
      { text: "Преобразую в текст...", Icon: FileSearch },
    ],
    []
  );

  // Сообщения для AI
  const thinkingMessages = useMemo(
    () => [
      { text: "Анализирую запрос...", Icon: Brain },
      { text: "Поиск информации...", Icon: Search },
      { text: "Изучаю данные...", Icon: BookOpen },
      { text: "Формирую ответ...", Icon: PenLine },
      { text: "Завершаю...", Icon: Loader2 },
    ],
    []
  );

  // Эффект для смены фазы STT
  useEffect(() => {
    if (isTranscribing) {
      setThinkingPhase(0);
      const interval = setInterval(() => {
        setThinkingPhase((prev) => (prev + 1) % sttMessages.length);
      }, 1500);
      return () => clearInterval(interval);
    }
  }, [isTranscribing, sttMessages.length]);

  // Эффект для смены фазы AI
  useEffect(() => {
    if (isThinking) {
      setThinkingPhase(0);
      const interval = setInterval(() => {
        setThinkingPhase((prev) => (prev + 1) % thinkingMessages.length);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isThinking, thinkingMessages.length]);

  // Сброс фазы когда ничего не происходит
  useEffect(() => {
    if (!isTranscribing && !isThinking) {
      setThinkingPhase(0);
    }
  }, [isTranscribing, isThinking]);

  // Эффект для запуска печатания нового сообщения от ассистента
  useEffect(() => {
    const messages = currentConversation?.messages || [];
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];

    // Если это сообщение от ассистента и еще не было напечатано
    if (
      lastMessage?.role === "assistant" &&
      !typedMessagesRef.current.has(lastMessage.id)
    ) {
      setTypingMessageId(lastMessage.id);
    }
  }, [currentConversation?.messages?.length]);

  // Скролл к последнему сообщению (только когда модалка открыта)
  useEffect(() => {
    if (isOpen && hasMessages) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isOpen, currentConversation?.messages, hasMessages]);

  // Скролл во время печатания
  useEffect(() => {
    if (typingMessageId) {
      const interval = setInterval(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      return () => clearInterval(interval);
    }
  }, [typingMessageId]);

  // Блокировка скролла body при открытой модалке
  useEffect(() => {
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      // Небольшая задержка для плавной анимации
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [isOpen]);

  // Автофокус на input при открытии
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Небольшая задержка для завершения анимации
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        if (showChatList) {
          setShowChatList(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, showChatList, onClose]);

  // Сброс состояния при закрытии
  useEffect(() => {
    if (!isOpen) {
      setTextInput("");
      setShowQuickCommands(true);
      setShowChatList(false);
    }
  }, [isOpen]);

  /*
   * Быстрая команда отправляется как обычный вопрос.
   *
   * Раньше кнопки были выключены: отвечать было нечем, ручка от
   * республиканского продукта у бэкенда Атырау отсутствует. Теперь запрос идёт
   * обычным путём, а при недоступном сервисе ответ приходит заготовкой,
   * помеченной как заготовка (см. `aiAssistantDemoAnswers`).
   */
  const runCommand = useCallback(
    (command: string) => {
      handleUserInput(command);
      setShowQuickCommands(false);
    },
    [handleUserInput],
  );

  // Мемоизированные обработчики
  const handleSend = useCallback(() => {
    if (textInput.trim()) {
      handleUserInput(textInput);
      setTextInput("");
      setShowQuickCommands(false);
    }
  }, [textInput, handleUserInput]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handlePopularCommand = useCallback(
    (command: string) => {
      handleUserInput(command);
      setShowQuickCommands(false);
    },
    [handleUserInput]
  );

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-0 ipad:p-4 safe-area-inset transition-all duration-300 ease-out ${
          isAnimating ? "opacity-100" : "opacity-0"
        }`}
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {/* Modal container */}
        <div
          ref={modalRef}
          className={`w-full h-[100dvh] ipad:h-[85dvh] ipad:max-w-6xl flex gap-0 ipad:gap-4 ipad:rounded-xl overflow-hidden transition-all duration-300 ease-out ${
            isAnimating
              ? "opacity-100 scale-100 translate-y-0"
              : "opacity-0 scale-95 translate-y-4"
          }`}
        >
          {/* Боковая панель с быстрыми командами */}
          <div className="hidden ipad:flex flex-col w-64 ipad-landscape:w-80 flex-shrink-0 min-h-0">
            <Card className="flex-1 flex flex-col shadow-xl min-h-0 overflow-hidden">
              <CardHeader className="flex-shrink-0 border-b bg-gradient-to-br from-blue-50 to-indigo-50 py-4 px-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-500 rounded-lg">
                    <Zap className="text-white" size={18} />
                  </div>
                  <CardTitle className="text-base font-semibold text-gray-900">
                    Быстрые команды
                  </CardTitle>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Нажмите для быстрого действия
                </p>
              </CardHeader>
              <CardContent className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2">
                {popularCommands.map((cmd) => {
                  const Icon = getCommandIcon(cmd.id);
                  return (
                    <button
                      key={cmd.id}
                      onClick={(e) => {
                        e.preventDefault();
                        runCommand(cmd.command);
                      }}
                      disabled={isProcessing || isListening}
                      className="w-full p-3 text-left bg-white rounded-lg border border-gray-200 transition-all group hover:border-blue-300 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="p-1.5 bg-blue-50 rounded-md transition-colors group-hover:bg-blue-100">
                          <Icon className="text-blue-600" size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 line-clamp-1">
                            {cmd.label}
                          </div>
                          {cmd.description && (
                            <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                              {cmd.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Основное окно чата */}
          <Card className="flex-1 flex flex-col shadow-2xl min-w-0 min-h-0 overflow-hidden rounded-none ipad:rounded-xl">
            <CardHeader className="flex-shrink-0 border-b bg-gradient-to-r from-blue-50 to-indigo-50 py-3 px-4 ipad:py-4 ipad:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 ipad:gap-3">
                  {/* Кнопка "Назад" для мобильных - иконка AI (кликабельная только если есть сообщения) */}
                  {hasMessages ? (
                    <button
                      onClick={() => setShowChatList(true)}
                      className="ipad:hidden p-1.5 hover:bg-gray-100 rounded-lg transition-colors active:scale-95"
                      title="Список чатов"
                      aria-label="Открыть список чатов"
                    >
                      <img
                        src={aiArtIcon}
                        alt="AI Ассистент"
                        className="w-7 h-7 object-contain"
                      />
                    </button>
                  ) : (
                    <img
                      src={aiArtIcon}
                      alt="AI Ассистент"
                      className="ipad:hidden w-7 h-7 object-contain"
                    />
                  )}

                  {/* Иконка для iPad+ (не кликабельная) */}
                  <img
                    src={aiArtIcon}
                    alt="AI Ассистент"
                    className="hidden ipad:block w-8 h-8 object-contain"
                  />
                  <div>
                    <CardTitle
                      id={titleId}
                      className="text-base ipad:text-xl font-semibold text-gray-900"
                    >
                      AI Ассистент
                    </CardTitle>
                    <p className="text-xs ipad:text-sm text-gray-600 mt-0.5">
                      {isListening ? "Слушаю..." : "Голосовой помощник"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 ipad:gap-2">

                  {/* Кнопка быстрых команд для мобильных */}
                  {hasMessages && (
                    <button
                      onClick={() => setShowQuickCommands(!showQuickCommands)}
                      className="ipad:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Быстрые команды"
                      aria-label="Показать быстрые команды"
                    >
                      <Zap
                        size={18}
                        className={
                          showQuickCommands ? "text-blue-600" : "text-gray-600"
                        }
                      />
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Закрыть"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="flex-1 min-h-0 overflow-hidden flex flex-col p-0">
              {/* Область сообщений */}
              <div className="flex-1 min-h-0 overflow-y-auto p-4 ipad:p-6 space-y-4 bg-gradient-to-b from-gray-50 to-white">
                {!hasMessages && (
                  <div className="flex flex-col items-center justify-center h-full py-12">
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-blue-200 rounded-full blur-xl opacity-30 animate-pulse"></div>
                      <div className="relative inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-sequence-primary to-sequence-dark rounded-full shadow-lg">
                        <img
                          src={aiArtIconWhite}
                          alt="AI Ассистент"
                          className="w-10 h-10 object-contain"
                        />
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Готов помочь вам
                    </h3>
                    <p className="text-sm text-gray-600 mb-8 text-center max-w-md">
                      Нажмите на микрофон для голосового ввода или введите
                      вопрос текстом
                    </p>

                    {/* Быстрые команды для мобильных (показываются только при первом открытии) */}
                    <div className="ipad:hidden w-full max-w-md space-y-2">
                      <div className="text-xs font-medium text-gray-500 mb-2 px-2">
                        Популярные запросы:
                      </div>
                      {popularCommands.slice(0, 4).map((cmd) => {
                        const Icon = getCommandIcon(cmd.id);
                        return (
                          <button
                            key={cmd.id}
                            onClick={(e) => {
                              e.preventDefault();
                            }}
                            disabled={isProcessing || isListening}
                            className="w-full p-3 text-left bg-white rounded-lg border border-gray-200 transition-all shadow-sm disabled:opacity-50"
                          >
                            <div className="flex items-center gap-2.5">
                              <Icon
                                className="text-gray-400 flex-shrink-0"
                                size={18}
                              />
                              <span className="text-sm font-medium text-gray-500">
                                {cmd.label}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {currentConversation?.messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex gap-3 ${
                      message.role === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    {message.role === "assistant" && (
                      <div className="flex-shrink-0 w-9 h-9 bg-gradient-to-br from-sequence-primary to-sequence-dark rounded-full flex items-center justify-center shadow-md">
                        <img
                          src={aiArtIconWhite}
                          alt="AI Ассистент"
                          className="w-5 h-5 object-contain"
                        />
                      </div>
                    )}
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-sm ${
                        message.role === "user"
                          ? "bg-gradient-to-br from-sequence-primary to-sequence-dark text-white"
                          : "bg-white text-gray-900 border border-gray-200"
                      }`}
                    >
                      <div className="text-sm leading-relaxed whitespace-pre-wrap">
                        {message.role === "assistant" && typingMessageId === message.id ? (
                          <TypewriterText
                            key={`typing-${message.id}`}
                            text={message.content}
                            speed={10}
                            onComplete={() => {
                              typedMessagesRef.current.add(message.id);
                              setTypingMessageId(null);
                            }}
                          />
                        ) : message.role === "assistant" ? (
                          <AgentText text={message.content} />
                        ) : (
                          message.content
                        )}
                      </div>
                      {message.role === "assistant" &&
                        !!message.steps?.length &&
                        typingMessageId !== message.id && <AgentSteps steps={message.steps} />}
                      {/*
                        * Уточняющие вопросы под ответом — по три на тему.
                        *
                        * Появляются ТОЛЬКО когда печать закончилась: кнопки под
                        * ещё набирающимся текстом человек нажимает раньше, чем
                        * дочитал ответ, и разговор скачет.
                        */}
                      {message.role === "assistant" &&
                        message.followUps?.length &&
                        typingMessageId !== message.id && (
                          <div className="mt-3 flex flex-col gap-1.5">
                            {message.followUps.map((f) => (
                              <button
                                key={f.id}
                                type="button"
                                onClick={() => runCommand(f.question)}
                                disabled={isProcessing || isListening}
                                className="text-left text-xs px-2.5 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 transition-colors disabled:opacity-50"
                              >
                                {f.question}
                              </button>
                            ))}
                          </div>
                        )}
                      <div className="flex items-center gap-2 mt-2">
                        {message.isVoice && (
                          <div className="text-xs opacity-70 flex items-center gap-1">
                            <Mic size={12} />
                            <span>Голос</span>
                          </div>
                        )}
                        <div className="text-xs opacity-70">
                          {message.timestamp.toLocaleTimeString("ru-RU", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </div>
                    </div>
                    {message.role === "user" && (
                      <div className="flex-shrink-0 w-9 h-9 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                        {/* Тот же аватар, что в шапке, — см. `utils/userAvatar`. */}
                        {userAvatar(user) ? (
                          <img
                            src={userAvatar(user)}
                            alt={user?.fullName || "Пользователь"}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-gray-600 text-xs font-semibold">
                            {user?.fullName?.charAt(0) || "П"}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Лоадер для STT (распознавание речи) */}
                {isTranscribing && (() => {
                  const currentStt = sttMessages[thinkingPhase % sttMessages.length];
                  const SttIcon = currentStt.Icon;
                  return (
                    <div className="flex gap-3 justify-start animate-fadeIn">
                      <div className="flex-shrink-0 w-9 h-9 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center shadow-md">
                        <Mic className="w-4 h-4 text-white" />
                      </div>
                      <div className="bg-white rounded-2xl px-4 py-3 border border-gray-200 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                            <SttIcon className="w-4 h-4 text-violet-600 animate-pulse" />
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-gray-800">
                              {currentStt.text}
                            </span>
                            <div className="flex gap-1 items-center">
                              <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-pulse" style={{ animationDelay: "0ms" }}></div>
                              <div className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-pulse" style={{ animationDelay: "200ms" }}></div>
                              <div className="w-1.5 h-1.5 bg-violet-600 rounded-full animate-pulse" style={{ animationDelay: "400ms" }}></div>
                            </div>
                          </div>
                          <button
                            onClick={stopTranscribing}
                            className="ml-2 p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                            title="Остановить"
                          >
                            <Square size={14} className="text-gray-500" fill="currentColor" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Лоадер для AI (обработка запроса) */}
                {isThinking && (() => {
                  const currentThink = thinkingMessages[thinkingPhase % thinkingMessages.length];
                  const ThinkIcon = currentThink.Icon;
                  return (
                    <div className="flex gap-3 justify-start animate-fadeIn">
                      <div className="flex-shrink-0 w-9 h-9 bg-gradient-to-br from-sequence-primary to-sequence-dark rounded-full flex items-center justify-center shadow-md">
                        <img
                          src={aiArtIconWhite}
                          alt="AI"
                          className="w-5 h-5 object-contain"
                        />
                      </div>
                      <div className="bg-white rounded-2xl px-4 py-3 border border-gray-200 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                            <ThinkIcon className={`w-4 h-4 text-blue-600 ${currentThink.Icon === Loader2 ? 'animate-spin' : 'animate-pulse'}`} />
                          </div>
                          <div className="flex flex-col gap-1">
                            {/*
                              Шаг агента, когда он пришёл: «Считаю обращения»
                              — правда о том, что идёт. Общая фраза — только
                              пока агент не сказал, чем занят.
                            */}
                            <span className="text-sm font-medium text-gray-800">
                              {agentStep ? `${agentStep}…` : currentThink.text}
                            </span>
                            <div className="flex gap-1 items-center">
                              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: "0ms" }}></div>
                              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: "200ms" }}></div>
                              <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse" style={{ animationDelay: "400ms" }}></div>
                            </div>
                          </div>
                          <button
                            onClick={stopThinking}
                            className="ml-2 p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                            title="Остановить"
                          >
                            <Square size={14} className="text-gray-500" fill="currentColor" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <div ref={messagesEndRef} />
              </div>

              {/* Быстрые команды для мобильных (показываются при нажатии на кнопку) */}
              {showQuickCommands && hasMessages && (
                <div className="flex-shrink-0 ipad:hidden border-t bg-gray-50 p-3 max-h-48 overflow-y-auto">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Быстрые команды
                    </div>
                    <button
                      onClick={() => setShowQuickCommands(false)}
                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                      aria-label="Скрыть команды"
                    >
                      <X size={14} className="text-gray-500" />
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {popularCommands.slice(0, 6).map((cmd) => {
                      const Icon = getCommandIcon(cmd.id);
                      return (
                        <button
                          key={cmd.id}
                          onClick={(e) => {
                            e.preventDefault();
                            runCommand(cmd.command);
                          }}
                          disabled={isProcessing || isListening}
                          className="w-full p-2.5 text-left bg-white rounded-lg border border-gray-200 transition-all disabled:opacity-50"
                        >
                          <div className="flex items-center gap-2">
                            <Icon
                              className="text-gray-400 flex-shrink-0"
                              size={16}
                            />
                            <span className="text-sm font-medium text-gray-500 line-clamp-1">
                              {cmd.label}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Панель ввода */}
              <div className="flex-shrink-0 border-t bg-white p-3 ipad:p-4">
                <div className="flex items-end gap-2">
                  {/* Текстовый ввод */}
                  <div className="flex-1 relative">
                    <input
                      ref={inputRef}
                      type="text"
                      value={textInput}
                      onChange={(e) => setTextInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={
                        isListening
                          ? "Говорите..."
                          : "Введите вопрос или нажмите микрофон..."
                      }
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-12 text-sm transition-all"
                      disabled={isProcessing || isListening}
                      aria-label="Введите сообщение"
                    />
                    {textInput && (
                      <button
                        onClick={handleSend}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 bg-gradient-to-br from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-md rounded-md flex items-center justify-center disabled:opacity-50"
                        disabled={isProcessing || isListening}
                      >
                        <Send size={16} className="text-white" />
                      </button>
                    )}
                  </div>

                  {/* Кнопка микрофона */}
                  <button
                    onClick={() => {
                      if (isListening) {
                        stopListening();
                      } else {
                        // Останавливаем TTS перед началом записи
                        if (isSpeaking) {
                          stopSpeaking();
                        }
                        startListening();
                      }
                    }}
                    className={`flex-shrink-0 transition-all p-3 rounded-md disabled:opacity-50 ${
                      isListening
                        ? "bg-red-500 hover:bg-red-600 animate-pulse shadow-lg scale-105"
                        : "bg-gradient-to-br from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-md"
                    }`}
                    disabled={isProcessing}
                  >
                    {isListening ? (
                      <MicOff className="text-white" size={20} />
                    ) : (
                      <Mic className="text-white" size={20} />
                    )}
                  </button>
                </div>

                {/* Индикатор прослушивания */}
                {isListening && (
                  <div className="mt-3 flex items-center justify-center gap-3 bg-white border border-gray-200 rounded-xl py-2.5 px-4 shadow-sm animate-fadeIn">
                    <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                      <div className="relative flex items-center justify-center">
                        <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-ping absolute"></div>
                        <div className="w-2.5 h-2.5 bg-red-500 rounded-full relative"></div>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-700">Слушаю... Говорите</span>
                  </div>
                )}

                {/* Индикатор воспроизведения TTS */}
                {isSpeaking && (
                  <div className="mt-3 flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-xl py-2.5 px-4 shadow-sm animate-fadeIn">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <Volume2 className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-700">Воспроизведение</span>
                        <div className="flex gap-0.5 items-end h-4">
                          <div className="w-1 bg-emerald-500 rounded-full animate-soundWave" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-1 bg-emerald-500 rounded-full animate-soundWave" style={{ animationDelay: '80ms' }}></div>
                          <div className="w-1 bg-emerald-500 rounded-full animate-soundWave" style={{ animationDelay: '160ms' }}></div>
                          <div className="w-1 bg-emerald-500 rounded-full animate-soundWave" style={{ animationDelay: '240ms' }}></div>
                          <div className="w-1 bg-emerald-500 rounded-full animate-soundWave" style={{ animationDelay: '320ms' }}></div>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={stopSpeaking}
                      className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                      title="Остановить"
                    >
                      <Square size={14} className="text-gray-500" fill="currentColor" />
                    </button>
                  </div>
                )}

                {/* Подсказка */}
                {!isListening && !isProcessing && !isSpeaking && !textInput && (
                  <div className="mt-2 text-xs text-gray-500 text-center flex items-center justify-center gap-2">
                    <span>Нажмите микрофон для голосового ввода или введите текст</span>
                    <button
                      onClick={toggleDebug}
                      className={`p-1 rounded transition-colors ${
                        showDebug ? "bg-orange-100 text-orange-600" : "hover:bg-gray-100 text-gray-400"
                      }`}
                      title="Показать логи"
                    >
                      <Bug size={14} />
                    </button>
                  </div>
                )}

                {/* Debug Panel */}
                {showDebug && (
                  <div className="mt-3 border border-orange-200 rounded-lg bg-orange-50 overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 bg-orange-100 border-b border-orange-200">
                      <div className="flex items-center gap-2">
                        <Bug size={14} className="text-orange-600" />
                        <span className="text-xs font-semibold text-orange-800">Debug Logs ({debugLogs.length})</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={clearLogs}
                          className="p-1 hover:bg-orange-200 rounded text-orange-600"
                          title="Очистить логи"
                        >
                          <Trash2 size={12} />
                        </button>
                        <button
                          onClick={toggleDebug}
                          className="p-1 hover:bg-orange-200 rounded text-orange-600"
                          title="Закрыть"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto p-2 space-y-2 text-xs font-mono">
                      {debugLogs.length === 0 ? (
                        <div className="text-center text-orange-400 py-4">
                          Нет логов. Нажмите на микрофон для записи.
                        </div>
                      ) : (
                        debugLogs.map((log) => (
                          <div
                            key={log.id}
                            className={`rounded overflow-hidden border ${
                              log.type === "error"
                                ? "bg-red-50 border-red-200"
                                : log.type === "success"
                                ? "bg-green-50 border-green-200"
                                : log.type === "warning"
                                ? "bg-yellow-50 border-yellow-200"
                                : "bg-white border-gray-200"
                            }`}
                          >
                            {/* Header */}
                            <div className={`px-2 py-1 flex items-center gap-2 ${
                              log.type === "error"
                                ? "bg-red-100"
                                : log.type === "success"
                                ? "bg-green-100"
                                : log.type === "warning"
                                ? "bg-yellow-100"
                                : "bg-gray-100"
                            }`}>
                              <span className="text-gray-500">
                                {log.timestamp.toLocaleTimeString("ru-RU", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  second: "2-digit",
                                })}.{String(log.timestamp.getMilliseconds()).padStart(3, '0')}
                              </span>
                              <span className={`font-semibold ${
                                log.type === "error"
                                  ? "text-red-700"
                                  : log.type === "success"
                                  ? "text-green-700"
                                  : log.type === "warning"
                                  ? "text-yellow-700"
                                  : "text-gray-700"
                              }`}>
                                {log.message}
                              </span>
                            </div>
                            {/* Data */}
                            {log.data && (
                              <div className="px-2 py-1.5 text-[10px] leading-relaxed overflow-x-auto">
                                <pre className="whitespace-pre-wrap break-all text-gray-600">
                                  {JSON.stringify(log.data, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Модальное окно со списком чатов (только для мобильных) */}
      {showChatList && (
        <div className="ipad:hidden fixed inset-0 bg-white z-[60] flex flex-col">
          {/* Header */}
          <div className="flex-shrink-0 border-b bg-gradient-to-r from-blue-50 to-indigo-50 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowChatList(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Назад к чату"
                >
                  <ArrowLeft size={20} className="text-gray-700" />
                </button>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    История чатов
                  </h2>
                  <p className="text-xs text-gray-600">Все ваши разговоры</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Закрыть"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Список чатов */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50">
            {/* Текущий чат */}
            {currentConversation && (
              <button
                onClick={() => setShowChatList(false)}
                className="w-full p-4 text-left bg-blue-50 border-2 border-blue-200 rounded-xl transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                    <MessageSquare className="text-white" size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-sm font-semibold text-gray-900">
                        Текущий чат
                      </h3>
                      <span className="text-xs text-blue-600 font-medium">
                        Активен
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 line-clamp-2">
                      {currentConversation.messages.length > 0
                        ? currentConversation.messages[
                            currentConversation.messages.length - 1
                          ].content
                        : "Новый разговор"}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="text-xs text-gray-500">
                        {currentConversation.messages.length} сообщений
                      </div>
                      <div className="text-xs text-gray-400">•</div>
                      <div className="text-xs text-gray-500">
                        {currentConversation.updatedAt.toLocaleTimeString(
                          "ru-RU",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            )}

            {/* Плейсхолдер для будущих чатов */}
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 rounded-full mb-3">
                <MessageSquare className="text-gray-400" size={24} />
              </div>
              <p className="text-sm text-gray-600">
                История чатов появится здесь
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Пока у вас только текущий разговор
              </p>
            </div>
          </div>

          {/* Footer с кнопкой нового чата */}
          <div className="flex-shrink-0 border-t bg-white p-4">
            <button
              onClick={() => {
                setShowChatList(false);
                // Здесь можно добавить логику создания нового чата
              }}
              className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium rounded-xl transition-all shadow-md active:scale-98"
            >
              + Новый чат
            </button>
          </div>
        </div>
      )}
    </>
  );
}
